import _ from 'lodash'
import { GridApi, IRowNode } from 'ag-grid-community'
import {
  GanttDisplayUnit,
  GanttParameterVO,
} from '../../../../../../../../domain/value-object/GanttParameterVO'
import {
  getOrganizationWorkingDayCalendar,
  OrganizationWorkingDayCalendarProps,
} from '../../../../../../../../lib/functions/organizationWorkingDayCalendar'
import { CalendarDateVO } from '../../../../../../../../domain/value-object/CalendarDateVO'
import DateVO from '../../../../../../../../vo/DateVO'
import store from '../../../../../../../../store'
import { getFinancialStartMonth } from '../../../../../../../../utils/date'
import { WbsItemTypeVO } from '../../../../../../../../domain/value-object/WbsItemTypeVO'
import { colorPalette } from '../../../../../../../style/colorPalette'
import objects from '../../../../../../../../utils/objects'

export const ACTUAL_BAR_HEIGHT = 3
export const ACTUAL_BAR_GAP = 8

// ガント基準日: 未指定なら「今日」とみなす。
// 「基準日が無い＝今日」のデフォルト判断はこの1関数に集約する。
export const resolveGanttBaseDate = (baseDate?: Date): Date =>
  baseDate ?? new Date()

export const isGanttSummaryType = (wbsItemType?: WbsItemTypeVO): boolean => {
  return !!(
    wbsItemType?.isWorkgroup() ||
    wbsItemType?.isProcess() ||
    wbsItemType?.isDeliverableList()
  )
}

export const isGanttSummaryVisualType = (
  visualType: GanttBarVisualType
): boolean =>
  visualType === 'workgroup' ||
  visualType === 'process' ||
  visualType === 'summary'

export type GanttBarVisualType =
  | 'workgroup'
  | 'process'
  | 'summary'
  | 'parentTask'
  | 'task'
  | 'deliverable'

export type ShapeStyle =
  | 'bar'
  | 'pill'
  | 'line-triangle'
  | 'arrow'
  | 'bar-triangle'

const BAR_HEIGHTS: Record<GanttBarVisualType, number> = {
  workgroup: 6,
  process: 26,
  summary: 8,
  parentTask: 12,
  task: 12,
  deliverable: 20,
}

const SHAPE_STYLES: Record<GanttBarVisualType, ShapeStyle> = {
  workgroup: 'line-triangle',
  process: 'arrow',
  summary: 'line-triangle',
  parentTask: 'bar-triangle',
  task: 'pill',
  deliverable: 'bar',
}

export const GANTT_BAR_COLORS = [
  'RED',
  'ORANGE',
  'YELLOW',
  'GREEN',
  'BLUE',
  'PURPLE',
] as const

export type GanttBarColorName = (typeof GANTT_BAR_COLORS)[number]

export const DEFAULT_GANTT_BAR_COLOR: GanttBarColorName = 'BLUE'

export const isGanttBarColorName = (
  value: string
): value is GanttBarColorName =>
  (GANTT_BAR_COLORS as readonly string[]).includes(value)

type GanttBarColor = { base: string; done: string }

type GanttBarColorGroup = 'process' | 'deliverable' | 'task'

const WORKGROUP_COLOR: GanttBarColor = {
  base: colorPalette.monotone[4],
  done: colorPalette.monotone[5],
}

const GANTT_BAR_COLOR_SETS: Record<
  GanttBarColorName,
  Record<GanttBarColorGroup, GanttBarColor>
> = {
  RED: {
    process: { base: '#C62828', done: '#B71C1C' },
    deliverable: { base: '#E57373', done: '#F44336' },
    task: { base: '#EF9A9A', done: '#EF5350' },
  },
  ORANGE: {
    process: { base: '#EF6C00', done: '#E65100' },
    deliverable: { base: '#FFB74D', done: '#FF9800' },
    task: { base: '#FFCC80', done: '#FFA726' },
  },
  YELLOW: {
    process: { base: '#E6B422', done: '#C79413' },
    deliverable: { base: '#FFD54F', done: '#FFC107' },
    task: { base: '#FFE082', done: '#FFCA28' },
  },
  GREEN: {
    process: { base: '#2E7D32', done: '#1B5E20' },
    deliverable: { base: '#81C784', done: '#4CAF50' },
    task: { base: '#A5D6A7', done: '#66BB6A' },
  },
  BLUE: {
    process: { base: colorPalette.blue[8], done: colorPalette.blue[10] },
    deliverable: { base: colorPalette.blue[4], done: colorPalette.blue[7] },
    task: { base: colorPalette.skyBlue[3], done: colorPalette.skyBlue[4] },
  },
  PURPLE: {
    process: { base: '#6A1B9A', done: '#4A148C' },
    deliverable: { base: '#BA68C8', done: '#9C27B0' },
    task: { base: '#CE93D8', done: '#AB47BC' },
  },
}

const COLOR_GROUP_BY_VISUAL_TYPE: Record<
  Exclude<GanttBarVisualType, 'workgroup'>,
  GanttBarColorGroup
> = {
  process: 'process',
  summary: 'deliverable',
  deliverable: 'deliverable',
  parentTask: 'task',
  task: 'task',
}

// 工程に設定した色は配下にも継承させるため、色を持つ最も近い祖先まで遡る
export const getInheritedGanttColor = (
  node: IRowNode | undefined,
  colors: ReadonlyMap<string, GanttBarColorName> | undefined,
  wbsItemUuidPath: string
): GanttBarColorName | undefined => {
  if (!colors?.size) return undefined
  let current: IRowNode | null | undefined = node
  while (current) {
    const wbsItemUuid = objects.getValue(current.data, wbsItemUuidPath)
    const color = wbsItemUuid ? colors.get(wbsItemUuid) : undefined
    if (color) return color
    current = current.parent
  }
  return undefined
}

export const getBarColorByType = (
  visualType: GanttBarVisualType,
  isDone: boolean,
  colorName?: GanttBarColorName
): string => {
  const pickColor = (color: GanttBarColor) => (isDone ? color.done : color.base)
  if (visualType === 'workgroup') return pickColor(WORKGROUP_COLOR)
  const colorSet = GANTT_BAR_COLOR_SETS[colorName ?? DEFAULT_GANTT_BAR_COLOR]
  return pickColor(colorSet[COLOR_GROUP_BY_VISUAL_TYPE[visualType]])
}

export const getGanttBarHeight = (visualType: GanttBarVisualType): number =>
  BAR_HEIGHTS[visualType]

export const getShapeStyle = (visualType: GanttBarVisualType): ShapeStyle =>
  SHAPE_STYLES[visualType]

export const buildArrowClipPath = (widthPercent: number): string => {
  const start = (100 - widthPercent).toFixed(2)
  return `polygon(0 0, ${start}% 0, 100% 50%, ${start}% 100%, 0 100%)`
}

export const getGanttBarVisualType = (
  wbsItemType: WbsItemTypeVO | undefined,
  hasChildren: boolean
): GanttBarVisualType => {
  if (wbsItemType?.isWorkgroup()) return 'workgroup'
  if (wbsItemType?.isProcess()) return 'process'
  if (wbsItemType?.isDeliverableList()) return 'summary'
  if (wbsItemType?.isTask() && hasChildren) return 'parentTask'
  if (wbsItemType?.isTask()) return 'task'
  return 'deliverable'
}

const getFiscalStartMonth = (): number =>
  getFinancialStartMonth(
    store.getState().tenant.organization?.financialYearEndMonth
  )

export const generateGanttScale = async (ganttParameter: GanttParameterVO) => {
  const fiscalStartMonth = getFiscalStartMonth()
  const [startDate, endDate] = fixRange(ganttParameter, fiscalStartMonth)
  const timeScale = await getCalendar(startDate, endDate)
  const displayTimeScale = generateDisplayScale(ganttParameter, timeScale)
  return [displayTimeScale, timeScale]
}

const fixRange = (
  ganttParameter: GanttParameterVO,
  fiscalStartMonth: number
): [Date, Date] => {
  const { unit, startDay, range } = ganttParameter
  return [
    GanttParameterVO.roundStartDate(
      range.start,
      unit,
      startDay,
      fiscalStartMonth
    ).toDate(),
    GanttParameterVO.roundEndDate(
      range.end,
      unit,
      startDay,
      fiscalStartMonth
    ).toDate(),
  ]
}

const getCalendar = async (
  start: Date,
  end: Date
): Promise<CalendarDateVO[]> => {
  const calendarResponse = await getOrganizationWorkingDayCalendar({
    startDate: start,
    endDate: end,
  })
  const result = calendarResponse.json as OrganizationWorkingDayCalendarProps[]
  const map = {}
  result.forEach(v => {
    const c = new CalendarDateVO({
      date: v.date,
      legal: v.legal,
      name: v.name,
      workHour: v.workHour,
    })
    map[c.date.getTime()] = c
  })
  const calendar: CalendarDateVO[] = []
  let date = CalendarDateVO.of(start)
  const endDate = CalendarDateVO.of(end)
  while (date.isBefore(endDate) || date.isEqual(endDate)) {
    const cal = map[date.date.getTime()]
    calendar.push(cal ?? date)
    const next = new Date(date.date)
    next.setDate(next.getDate() + 1)
    date = CalendarDateVO.of(next)
  }
  return calendar
}

const generateDisplayScale = (
  ganttParameter: GanttParameterVO,
  timeScale: CalendarDateVO[]
): CalendarDateVO[] => {
  if (ganttParameter.unit === GanttDisplayUnit.DAY) {
    return _.cloneDeep(timeScale)
  }
  if (ganttParameter.unit === GanttDisplayUnit.WEEK) {
    const scale: CalendarDateVO[] = []
    let date = timeScale[0]
    let i = 0
    while (!!date) {
      scale.push(timeScale[i])
      i += 7
      date = timeScale[i]
    }
    return scale
  }
  if (ganttParameter.unit === GanttDisplayUnit.MONTH) {
    const scale: CalendarDateVO[] = []
    const end = timeScale[timeScale.length - 1]
    let date = timeScale[0]
    while (date.isBefore(end)) {
      scale.push(date)
      date = CalendarDateVO.of(new DateVO(date.date).addMonths(1).toDate())
    }
    return scale
  }
  if (ganttParameter.unit === GanttDisplayUnit.QUARTER) {
    const scale: CalendarDateVO[] = []
    const end = timeScale[timeScale.length - 1]
    let date = timeScale[0]
    while (date.isBefore(end)) {
      scale.push(date)
      date = CalendarDateVO.of(new DateVO(date.date).addQuarters(1).toDate())
    }
    return scale
  }
  return []
}

export const calcOptimizedGanttColumnWidth = (
  api: GridApi,
  viewportEl: HTMLElement | null,
  contentWidth: number,
  minContentRatio: number
): number | undefined => {
  if (!viewportEl) return undefined
  const otherColumnsWidth = api
    .getDisplayedCenterColumns()
    .filter(c => c.getColId() !== 'ganttChart')
    .reduce((sum, c) => sum + c.getActualWidth(), 0)
  const availableGanttWidth = viewportEl.clientWidth - otherColumnsWidth
  if (availableGanttWidth <= 0) return undefined
  return Math.max(availableGanttWidth, contentWidth * minContentRatio)
}

export const scaleGanttColumnWidth = (
  api: GridApi,
  prevContentWidth: number | undefined,
  newContentWidth: number,
  unitChanged: boolean
): void => {
  if (!prevContentWidth || prevContentWidth === newContentWidth) return
  const ganttCol = api.getColumn('ganttChart')
  if (!ganttCol) return
  if (unitChanged) {
    api.setColumnWidths([{ key: 'ganttChart', newWidth: newContentWidth }])
    return
  }
  const currentWidth = ganttCol.getActualWidth()
  const newWidth = Math.round(
    currentWidth * (newContentWidth / prevContentWidth)
  )
  api.setColumnWidths([{ key: 'ganttChart', newWidth }])
}
